/**
 * Tutorial Module — Guided Tour / Walkthrough
 *
 * API:
 *   Tutorial.start({
 *     delay: 2000,                          // opóźnienie startu w ms (opcjonalne)
 *     intro: {
 *       title: 'Chcesz zobaczyć, jak zmieniła się strona Winston?',
 *       text: 'Przygotowaliśmy dla Ciebie 3 krótkie podpowiedzi.',
 *       startLabel: 'Zaczynamy!',
 *       position: 'bottom-right'            // 'center' (default) | 'bottom-right'
 *     },
 *     steps: [
 *       {
 *         target: '.navbar__points',        // CSS selector (opcjonalny — brak = wycentrowany)
 *         mobilePosition: 'below',          // 'above' | 'below' (default: 'below')
 *         title: 'Punkty lojalnościowe',
 *         text: 'Tutaj znajdziesz aktualne saldo <strong>Punktów Win</strong>...'
 *       },
 *       { ... },
 *       {
 *         target: '.some-element',
 *         title: 'Win Club',
 *         text: '...',
 *         finishLabel: 'Odbierz dodatkowe punkty!'   // CTA na ostatnim kroku
 *       }
 *     ],
 *     onFinish: function() { ... },
 *     onClose: function() { ... }
 *   });
 *
 *   Tutorial.stop();
 */

(function () {
    'use strict';

    // ----------------------------------------
    // State
    // ----------------------------------------
    var state = {
        config: null,
        backdrop: null,
        dialog: null,
        currentStep: -1, // -1 = intro
        spotlightEl: null,
        spotlightClone: null,
        resizeTimer: null,
        delayTimer: null
    };

    // ----------------------------------------
    // Constants
    // ----------------------------------------
    var DIALOG_GAP = 20; // px between element and dialog
    var MOBILE_BP = 768;
    var SCROLL_SETTLE_DELAY = 1000; // ms — wait for smooth scroll + CSS reveal animations

    // SVG icons
    var ICON_HELP = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="15" stroke="white" stroke-width="2" fill="none"/><text x="16" y="22" text-anchor="middle" fill="white" font-family="Futura Now Text, sans-serif" font-size="18" font-weight="700">?</text></svg>';

    var ICON_ARROW = '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="12" viewBox="0 0 31 14" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M23.7344 0.162981C23.9484 -0.0504972 24.2936 -0.0544477 24.5117 0.152239L24.5225 0.162981L30.6172 6.24892C30.8307 6.46246 30.8338 6.80569 30.627 7.02333L30.6172 7.03407L24.5107 13.1366C24.2935 13.3537 23.9413 13.3543 23.7236 13.1376C23.5094 12.9242 23.5059 12.5801 23.7129 12.3622L23.7227 12.3515L28.8574 7.21962H0.570312C0.25564 7.21962 0.00021105 6.96491 0 6.65029C0 6.35417 0.225836 6.11075 0.514648 6.0829L0.570312 6.07997H28.873L23.7344 0.948137C23.5206 0.734449 23.5174 0.390373 23.7246 0.172747L23.7344 0.162981Z" fill="currentColor"/></svg>';

    // ----------------------------------------
    // Helpers
    // ----------------------------------------
    function isMobile() {
        return window.innerWidth < MOBILE_BP;
    }

    function createElement(tag, className, attrs) {
        var el = document.createElement(tag);
        if (className) el.className = className;
        if (attrs) {
            for (var key in attrs) {
                if (attrs.hasOwnProperty(key)) {
                    el.setAttribute(key, attrs[key]);
                }
            }
        }
        return el;
    }

    /**
     * Find the first VISIBLE element matching selector.
     * Falls back to first match if none are visible.
     * Handles duplicated navbar elements (mobile + desktop).
     */
    function findVisibleElement(selector) {
        var elements = document.querySelectorAll(selector);
        if (!elements.length) return null;
        if (elements.length === 1) return elements[0];

        for (var i = 0; i < elements.length; i++) {
            var el = elements[i];
            if (el.offsetWidth > 0 && el.offsetHeight > 0) {
                return el;
            }
        }
        // Fallback: return first match
        return elements[0];
    }

    // ----------------------------------------
    // Build Intro Dialog
    // ----------------------------------------
    function buildIntroDialog() {
        var intro = state.config.intro;
        var posClass = intro.position === 'bottom-right' ? ' tutorial-dialog--intro-br' : '';
        var dialog = createElement('div', 'tutorial-dialog tutorial-dialog--intro' + posClass);
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');

        // Close button (absolute positioned)
        var closeBtn = buildCloseButton();
        dialog.appendChild(closeBtn);

        // Icon (direct child — same as step)
        var icon = createElement('div', 'tutorial-dialog__icon');
        icon.innerHTML = ICON_HELP;
        dialog.appendChild(icon);

        // Body (same wrapper as step)
        var body = createElement('div', 'tutorial-dialog__body');

        if (intro.title) {
            var title = createElement('h2', 'tutorial-dialog__title');
            title.textContent = intro.title;
            body.appendChild(title);
        }

        if (intro.text) {
            var text = createElement('p', 'tutorial-dialog__text');
            text.innerHTML = intro.text;
            body.appendChild(text);
        }

        // Start / close button
        var hasSteps = state.config.steps && state.config.steps.length > 0;
        var startBtn = createElement('button', 'tutorial-dialog__start btn btn-secondary-negative');
        startBtn.textContent = intro.startLabel || (hasSteps ? 'Zaczynamy!' : 'Zamknij');
        startBtn.addEventListener('click', function () {
            if (hasSteps) {
                goToStep(0);
            } else {
                if (typeof state.config.onFinish === 'function') {
                    state.config.onFinish();
                }
                stop();
            }
        });
        body.appendChild(startBtn);

        dialog.appendChild(body);

        return dialog;
    }

    // ----------------------------------------
    // Build Step Dialog
    // ----------------------------------------
    function buildStepDialog(stepIndex) {
        var step = state.config.steps[stepIndex];
        var totalSteps = state.config.steps.length;
        var isLast = stepIndex === totalSteps - 1;
        var hasTarget = !!step.target;

        var classes = 'tutorial-dialog tutorial-dialog--step';
        if (!hasTarget) {
            classes += ' tutorial-dialog--centered';
        }

        var dialog = createElement('div', classes);
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');

        // Icon
        var icon = createElement('div', 'tutorial-dialog__icon');
        icon.innerHTML = ICON_HELP;
        dialog.appendChild(icon);

        // Body
        var body = createElement('div', 'tutorial-dialog__body');

        if (step.title) {
            var title = createElement('h3', 'tutorial-dialog__title');
            title.textContent = step.title;
            body.appendChild(title);
        }

        if (step.text) {
            var text = createElement('p', 'tutorial-dialog__text');
            text.innerHTML = step.text;
            body.appendChild(text);
        }

        // Footer: counter + arrows (hide for single-step tutorials)
        if (totalSteps > 1) {
            var footer = createElement('div', 'tutorial-dialog__footer');

            var counter = createElement('span', 'tutorial-dialog__counter');
            counter.textContent = (stepIndex + 1) + '/' + totalSteps;
            footer.appendChild(counter);

            var arrows = createElement('div', 'tutorial-dialog__arrows');

            // Prev arrow
            var prevBtn = createElement('button', 'tutorial-dialog__arrow tutorial-dialog__arrow--prev');
            prevBtn.innerHTML = ICON_ARROW;
            prevBtn.setAttribute('aria-label', 'Poprzedni krok');
            if (stepIndex === 0) {
                prevBtn.disabled = true;
            }
            prevBtn.addEventListener('click', function () {
                if (stepIndex > 0) {
                    goToStep(stepIndex - 1);
                }
            });
            arrows.appendChild(prevBtn);

            // Next arrow
            var nextBtn = createElement('button', 'tutorial-dialog__arrow tutorial-dialog__arrow--next');
            nextBtn.innerHTML = ICON_ARROW;
            nextBtn.setAttribute('aria-label', 'Następny krok');
            if (isLast) {
                nextBtn.disabled = true;
            }
            nextBtn.addEventListener('click', function () {
                if (!isLast) {
                    goToStep(stepIndex + 1);
                }
            });
            arrows.appendChild(nextBtn);

            footer.appendChild(arrows);
            body.appendChild(footer);
        }

        // CTA button (last step)
        if (isLast && step.finishLabel) {
            var cta = createElement('button', 'tutorial-dialog__cta');
            cta.textContent = step.finishLabel;
            cta.addEventListener('click', function () {
                if (typeof state.config.onFinish === 'function') {
                    state.config.onFinish();
                }
                stop();
            });
            body.appendChild(cta);
        }

        dialog.appendChild(body);

        return dialog;
    }

    // ----------------------------------------
    // Build Close Button
    // ----------------------------------------
    function buildCloseButton() {
        var btn = createElement('button', 'tutorial-dialog__close');
        btn.setAttribute('aria-label', 'Zamknij samouczek');
        btn.addEventListener('click', function () {
            if (typeof state.config.onClose === 'function') {
                state.config.onClose();
            }
            stop();
        });
        return btn;
    }

    // ----------------------------------------
    // Spotlight (clone-based with wrapper container)
    // Creates a wrapper div with white bg + padding + rounded corners,
    // then puts the cloned element inside. This prevents bg conflicts
    // with both dark and light elements.
    // ----------------------------------------

    var SPOTLIGHT_PAD = 6; // px padding around element

    function applySpotlight(selector) {
        removeSpotlight();

        if (!selector) return null;

        var el = findVisibleElement(selector);
        if (!el) return null;

        state.spotlightEl = el;

        var clone = el.cloneNode(true);
        var rect = el.getBoundingClientRect();
        var cs = window.getComputedStyle(el);

        // Style the clone to look identical, using original dimensions
        clone.style.cssText =
            'position:relative!important;' +
            'width:' + rect.width + 'px!important;' +
            'height:' + rect.height + 'px!important;' +
            'margin:0!important;' +
            'box-sizing:border-box!important;' +
            'background-color:' + cs.backgroundColor + '!important;' +
            'color:' + cs.color + '!important;' +
            'font-size:' + cs.fontSize + '!important;' +
            'font-family:' + cs.fontFamily + '!important;' +
            'font-weight:' + cs.fontWeight + '!important;' +
            'line-height:' + cs.lineHeight + '!important;' +
            'border-radius:' + cs.borderRadius + '!important;' +
            'padding:' + cs.padding + '!important;' +
            'display:' + cs.display + '!important;' +
            'align-items:' + cs.alignItems + '!important;' +
            'justify-content:' + cs.justifyContent + '!important;' +
            'gap:' + cs.gap + '!important;' +
            'pointer-events:none!important;';

        // Create wrapper container
        var wrapper = document.createElement('div');
        wrapper.className = 'tutorial-spotlight-wrapper';
        wrapper.style.cssText =
            'position:fixed;' +
            'top:' + (rect.top - SPOTLIGHT_PAD) + 'px;' +
            'left:' + (rect.left - SPOTLIGHT_PAD) + 'px;' +
            'padding:' + SPOTLIGHT_PAD + 'px;' +
            'z-index:402;' +
            'pointer-events:none;';

        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);
        state.spotlightClone = wrapper;

        return el;
    }

    /**
     * Reposition the wrapper to match original element's current coords.
     */
    function repositionSpotlightClone() {
        if (!state.spotlightEl || !state.spotlightClone) return;

        var rect = state.spotlightEl.getBoundingClientRect();
        state.spotlightClone.style.top = (rect.top - SPOTLIGHT_PAD) + 'px';
        state.spotlightClone.style.left = (rect.left - SPOTLIGHT_PAD) + 'px';
    }

    function removeSpotlight() {
        if (state.spotlightClone && state.spotlightClone.parentNode) {
            state.spotlightClone.parentNode.removeChild(state.spotlightClone);
        }
        state.spotlightClone = null;
        state.spotlightEl = null;
    }

    // ----------------------------------------
    // Position Dialog
    // ----------------------------------------
    function positionDialog(dialog, targetEl, mobilePos) {
        if (!targetEl) return; // centered dialogs — CSS handles positioning

        var rect = targetEl.getBoundingClientRect();
        var dialogRect = dialog.getBoundingClientRect();
        var vw = window.innerWidth;
        var vh = window.innerHeight;
        // Determine position: prefer mobilePos hint on mobile, otherwise 'below'.
        // Always auto-flip when there's not enough space below.
        var position = 'below';
        var spaceBelow = vh - rect.bottom - DIALOG_GAP;
        var spaceAbove = rect.top - DIALOG_GAP;

        if (isMobile()) {
            position = mobilePos || 'below';
            // Auto-flip ONLY when no explicit mobilePosition was provided
            if (!mobilePos) {
                if (position === 'below' && spaceBelow < dialogRect.height && spaceAbove > dialogRect.height) {
                    position = 'above';
                }
            }
        } else {
            // Desktop: default below, flip to above if not enough space below
            if (spaceBelow < dialogRect.height && spaceAbove > dialogRect.height) {
                position = 'above';
            }
        }

        var top, left;

        if (position === 'above') {
            top = rect.top - dialogRect.height - DIALOG_GAP;
        } else {
            top = rect.bottom + DIALOG_GAP;
        }

        if (isMobile()) {
            // Mobile: centered horizontally, full width minus margins
            left = 16;
        } else {
            // Desktop: align left edge with element, clamp to viewport
            left = rect.left;

            // Clamp right
            if (left + dialogRect.width > vw - 16) {
                left = vw - dialogRect.width - 16;
            }
            // Clamp left
            if (left < 16) {
                left = 16;
            }
        }

        // Clamp top within viewport
        if (top < 8) top = 8;
        if (top + dialogRect.height > vh - 8) {
            top = vh - dialogRect.height - 8;
        }

        dialog.style.top = top + 'px';
        dialog.style.left = left + 'px';
    }

    // ----------------------------------------
    // Scroll target into view (calls callback after scroll settles)
    // ----------------------------------------
    function scrollToTarget(targetEl, mobilePos, callback) {
        if (!targetEl) {
            if (callback) callback();
            return;
        }

        var rect = targetEl.getBoundingClientRect();
        var vh = window.innerHeight;
        // Reserve space for the dialog (~200px) + gap so dialog won't overlap element
        var dialogReserve = 200 + DIALOG_GAP;
        var hasRoomAbove = rect.top >= dialogReserve;
        var hasRoomBelow = (vh - rect.bottom) >= dialogReserve;
        var isInViewport = rect.top >= 0 && rect.bottom <= vh;

        // Scroll if element is outside viewport OR if there's no room for dialog on either side
        var needsScroll = !isInViewport || (!hasRoomAbove && !hasRoomBelow);

        // When dialog should appear above, also scroll if no room above specifically
        if (isInViewport && isMobile() && mobilePos === 'above' && !hasRoomAbove) {
            needsScroll = true;
        }

        if (needsScroll) {
            // Manual scroll with extra offset for breathing room
            var scrollExtra = 20;
            var absTop = rect.top + window.pageYOffset;
            var scrollY;

            if (isMobile() && mobilePos === 'above') {
                // Push element toward bottom → room above for dialog
                scrollY = absTop - vh + rect.height + scrollExtra;
            } else {
                // Center element, nudge slightly up for room below
                scrollY = absTop - (vh / 2) + (rect.height / 2) + scrollExtra;
            }

            window.scrollTo({
                top: Math.max(0, scrollY),
                behavior: 'smooth'
            });

            // Wait for scroll + element animations to finish
            setTimeout(function () {
                if (callback) callback();
            }, SCROLL_SETTLE_DELAY);
        } else {
            // Element is visible with room for dialog, but may still be
            // mid-animation (e.g. js-reveal CSS transitions). Wait for
            // running animations/transitions to finish before presenting.
            var animations = targetEl.getAnimations ? targetEl.getAnimations({ subtree: true }) : [];
            if (animations.length > 0) {
                Promise.all(animations.map(function (a) { return a.finished; }))
                    .then(function () { if (callback) callback(); });
            } else {
                if (callback) callback();
            }
        }
    }

    // ----------------------------------------
    // Show Dialog
    // ----------------------------------------
    function showDialog(dialog) {
        // Remove old dialog
        if (state.dialog && state.dialog.parentNode) {
            state.dialog.parentNode.removeChild(state.dialog);
        }

        document.body.appendChild(dialog);
        state.dialog = dialog;

        // Trigger reflow
        void dialog.offsetHeight;

        dialog.classList.add('is-visible');

        // Focus dialog
        dialog.setAttribute('tabindex', '-1');
        // dialog.focus();
    }

    // ----------------------------------------
    // Present spotlight + dialog (after scroll)
    // ----------------------------------------
    function presentStep(index, targetEl) {
        // Guard: tutorial may have been stopped while scrolling
        if (!state.config) return;

        var step = state.config.steps[index];

        // Apply spotlight AFTER scroll so clone position is correct
        if (step.target && targetEl) {
            applySpotlight(step.target);
        } else {
            removeSpotlight();
        }

        // Build & show dialog
        var dialog = buildStepDialog(index);
        showDialog(dialog);

        // Position dialog relative to target
        if (targetEl) {
            setTimeout(function () {
                positionDialog(dialog, targetEl, step.mobilePosition);
            }, 50);
        }
    }

    // ----------------------------------------
    // Go to step
    // ----------------------------------------
    function goToStep(index) {
        if (!state.config || index < 0 || index >= state.config.steps.length) return;

        state.currentStep = index;
        var step = state.config.steps[index];

        // Remove previous spotlight before scrolling
        removeSpotlight();

        // Remove previous dialog
        if (state.dialog && state.dialog.parentNode) {
            state.dialog.parentNode.removeChild(state.dialog);
            state.dialog = null;
        }

        var targetEl = step.target ? findVisibleElement(step.target) : null;

        if (targetEl) {
            // Scroll first, then present spotlight + dialog
            scrollToTarget(targetEl, step.mobilePosition, function () {
                presentStep(index, targetEl);
            });
        } else {
            // No target — present immediately (centered)
            presentStep(index, null);
        }
    }

    // ----------------------------------------
    // Resize handler
    // ----------------------------------------
    function handleResize() {
        clearTimeout(state.resizeTimer);
        state.resizeTimer = setTimeout(function () {
            // Reposition spotlight clone
            repositionSpotlightClone();

            if (state.currentStep >= 0 && state.config) {
                var step = state.config.steps[state.currentStep];
                if (step && step.target && state.dialog) {
                    var targetEl = findVisibleElement(step.target);
                    if (targetEl) {
                        positionDialog(state.dialog, targetEl, step.mobilePosition);
                    }
                }
            }
        }, 100);
    }

    // ----------------------------------------
    // Keydown handler
    // ----------------------------------------
    function handleKeydown(e) {
        if (e.key === 'Escape') {
            if (typeof state.config.onClose === 'function') {
                state.config.onClose();
            }
            stop();
        }

        // Arrow keys navigation within steps
        if (state.currentStep >= 0 && state.config) {
            if (e.key === 'ArrowRight' && state.currentStep < state.config.steps.length - 1) {
                goToStep(state.currentStep + 1);
            }
            if (e.key === 'ArrowLeft' && state.currentStep > 0) {
                goToStep(state.currentStep - 1);
            }
        }
    }

    // ----------------------------------------
    // Start
    // ----------------------------------------
    function start(config) {
        if (!config || (!config.intro && (!config.steps || !config.steps.length))) return;

        // Delayed start
        if (config.delay && config.delay > 0) {
            var delay = config.delay;
            delete config.delay; // prevent re-delay on internal calls
            state.delayTimer = setTimeout(function () {
                start(config);
            }, delay);
            return;
        }

        // Clean up any existing tutorial
        stop();

        state.config = config;
        state.currentStep = -1;

        // Create backdrop
        var backdrop = createElement('div', 'tutorial-backdrop');
        document.body.appendChild(backdrop);
        state.backdrop = backdrop;

        // Scroll lock — compensate for scrollbar width to prevent layout shift
        var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = scrollbarWidth + 'px';
        document.body.classList.add('modal-open');

        // Listeners
        document.addEventListener('keydown', handleKeydown);
        window.addEventListener('resize', handleResize);

        // Activate backdrop
        void backdrop.offsetHeight;
        backdrop.classList.add('is-active');

        // Backdrop click does NOT close the tutorial
        // — user must navigate through all steps or use the X close button

        // Show intro or first step
        if (config.intro) {
            var introDialog = buildIntroDialog();
            showDialog(introDialog);
        } else {
            goToStep(0);
        }
    }

    // ----------------------------------------
    // Stop
    // ----------------------------------------
    function stop() {
        // Remove dialog
        if (state.dialog && state.dialog.parentNode) {
            state.dialog.parentNode.removeChild(state.dialog);
        }

        // Remove spotlight
        removeSpotlight();

        // Remove backdrop with animation
        if (state.backdrop) {
            var backdrop = state.backdrop;
            backdrop.classList.remove('is-active');
            backdrop.classList.add('is-dismissing');

            setTimeout(function () {
                if (backdrop.parentNode) {
                    backdrop.parentNode.removeChild(backdrop);
                }
            }, 350);
        }

        // Scroll unlock
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');

        // Remove listeners
        document.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('resize', handleResize);
        clearTimeout(state.resizeTimer);
        clearTimeout(state.delayTimer);

        // Reset state
        state.config = null;
        state.backdrop = null;
        state.dialog = null;
        state.currentStep = -1;
        state.spotlightEl = null;
        state.spotlightClone = null;
        state.delayTimer = null;
    }

    // ----------------------------------------
    // Global API
    // ----------------------------------------
    window.Tutorial = {
        start: start,
        stop: stop
    };

})();
